import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { SWRConfig, SWRConfiguration } from 'swr';

import { AppRoutes } from './AppRoutes';
import { DefaultErrorBoundary } from '@/shared/components/ErrorFallback';
import { APP_VERSION } from './constants.client';
import { ThemeProvider } from './contexts/theme-context';
import { useSessionStore } from '@/stores/session-store';
import { Toast } from './ui/Toast';
import { onErrorRetry } from '@/utils/swr-retry';

const customSwrConfig: SWRConfiguration = {
  revalidateIfStale: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  revalidateOnMount: true,
  errorRetryCount: 3,

  // Once the session is gone, stop asking. SWR consults isPaused on every revalidation
  // path — mount, focus, reconnect, refreshInterval, mutate and the retry ladder — so
  // this one line halts the polling that errorRetryCount never bounded (sessions every
  // 5s, usage every 15s, session detail every 30s).
  isPaused: () => useSessionStore.getState().expired,

  // Skips the 4xx that will never fix themselves, but keeps 408/429 retryable.
  // See utils/swr-retry.
  onErrorRetry,
};

export const App = () => {
  return (
    <ThemeProvider>
      <DefaultErrorBoundary>
        <HelmetProvider>
          <Helmet>
            <link href="/favicon.svg" rel="shortcut icon" type="image/x-icon"></link>
            <meta name="app:version" content={APP_VERSION ?? 'dev'} />
          </Helmet>

          <SWRConfig value={customSwrConfig}>
            <DefaultErrorBoundary>
              <AppRoutes />
            </DefaultErrorBoundary>

            <Toaster position="bottom-right" gutter={8}>
              {(t) => <Toast t={t} />}
            </Toaster>
          </SWRConfig>
        </HelmetProvider>
      </DefaultErrorBoundary>
    </ThemeProvider>
  );
};
